// src/contexts/AuthContext.tsx
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { config } from '../utils/config';

interface User {
  id: number;
  username: string;
  email: string;
  full_name?: string;
  company?: string;
}

interface RegistrationResult {
  requires_verification: boolean;
  email?: string;
  message?: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  /** Resolves to the route this login should land on (see landingRouteForLogin). */
  login: (username: string, password: string) => Promise<string>;
  register: (username: string, email: string, password: string, fullName?: string, company?: string, selectedPlan?: string) => Promise<RegistrationResult>;
  logout: () => void;
  user: User | null;
  loginError: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Where a successful login lands. The Dashboard is home; brand-new accounts get
// the Guide first, for this many logins, because it explains the upload → review
// → invoice flow that the Dashboard assumes you already know.
const GUIDE_LANDING_LOGINS = 3;

// Counted per username: a shared browser must not spend one person's guide
// logins on another. The count lives in localStorage, so a new browser starts it
// over — showing the Guide again on an unfamiliar device is the harmless side.
const guideLandingKey = (username: string) => `guideLandings:${username.trim().toLowerCase()}`;

/** Record this login and return the route it should land on. */
const landingRouteForLogin = (username: string): string => {
  const key = guideLandingKey(username);
  const stored = Number.parseInt(localStorage.getItem(key) ?? '', 10);
  // Accounts that predate this counter already saw the Guide on first login
  // (the old 'hasSeenGuide' flag) — they are not owed three more.
  const previousLogins = Number.isFinite(stored)
    ? stored
    : (localStorage.getItem('hasSeenGuide') ? GUIDE_LANDING_LOGINS : 0);

  localStorage.setItem(key, String(previousLogins + 1));
  return previousLogins < GUIDE_LANDING_LOGINS ? '/guide' : '/dashboard';
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [user, setUser] = useState<User | null>(null);
  const [loginError, setLoginError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Check if user is already authenticated on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      setIsAuthenticated(true);

      // Try to fetch user details with the stored token
      fetch(`${config.API_URL}${config.endpoints.auth.profile}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      .then(response => {
        if (response.ok) {
          return response.json();
        } else {
          // Token is invalid, clear it
          localStorage.removeItem('token');
          setIsAuthenticated(false);
          throw new Error('Invalid token');
        }
      })
      .then(userData => {
        setUser(userData);
      })
      .catch(error => {
        console.error('Failed to fetch user data:', error);
        // Keep user logged in even if profile fetch fails
      })
      .finally(() => {
        setIsLoading(false);
      });
    } else {
      setIsLoading(false);
    }
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    setLoginError(null);
    try {
      // Try different approach - using URLSearchParams for form data
      const formData = new URLSearchParams();
      formData.append('username', username);
      formData.append('password', password);

      // Make the request using URLSearchParams for form data
      const response = await fetch(`${config.API_URL}${config.endpoints.auth.login}`, {
        method: 'POST',
        body: formData,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Accept': 'application/json',
        }
      });
      
      if (!response.ok) {
        // Handle error response
        const errorText = await response.text();
        let errorMessage = 'Authentication failed';
        
        try {
          if (errorText) {
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.detail || errorMessage;
          }
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }
        
        setLoginError(errorMessage);
        throw new Error(errorMessage);
      }
      
      // Parse the successful response
      const responseText = await response.text();
      let data;

      if (responseText && responseText.trim()) {
        try {
          data = JSON.parse(responseText);
        } catch (e) {
          setLoginError('Server returned invalid response format');
          throw new Error('Server returned invalid response format');
        }
      } else {
        // Empty response is an error - backend should always return token
        setLoginError('Login failed: Server returned empty response. Please try again.');
        throw new Error('Empty response from server');
      }

      // Validate we have an access token
      if (!data.access_token) {
        setLoginError('Login failed: Invalid server response');
        throw new Error('Invalid login response: missing access token');
      }
      
      // Store the token
      localStorage.setItem('token', data.access_token);
      setIsAuthenticated(true);
      
      // Try to fetch user details
      try {
        const userResponse = await fetch(`${config.API_URL}${config.endpoints.auth.profile}`, {
          headers: {
            'Authorization': `Bearer ${data.access_token}`
          }
        });

        if (userResponse.ok) {
          const userData = await userResponse.json();
          setUser(userData);
        }
      } catch (userError) {
        // Continue even if user fetch fails
      }

      // Returned rather than navigated to: the caller knows whether the user was
      // bounced here from a deep link, which takes precedence over the landing
      // route. Navigating here as well used to be pointless — LoginPage's own
      // navigate ran straight after and won, so the Guide redirect never fired.
      return landingRouteForLogin(username);

    } catch (error) {
      throw error;
    }
  }, []);

  const register = useCallback(async (username: string, email: string, password: string, fullName?: string, company?: string, selectedPlan?: string): Promise<RegistrationResult> => {
    setLoginError(null);
    try {
      const formData = new URLSearchParams();
      formData.append('username', username);
      formData.append('email', email);
      formData.append('password', password);
      if (fullName) formData.append('full_name', fullName);
      if (company) formData.append('company', company);
      if (selectedPlan) formData.append('selected_plan', selectedPlan);

      const response = await fetch(`${config.API_URL}${config.endpoints.auth.register}`, {
        method: 'POST',
        body: formData,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Accept': 'application/json',
        },
        mode: 'cors',
        credentials: 'omit'
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Registration failed';

        try {
          if (errorText) {
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.detail || errorData.message || errorMessage;
          }
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }

        setLoginError(errorMessage);
        throw new Error(errorMessage);
      }

      // Try to get response as text first
      const responseText = await response.text();

      let data;

      if (responseText && responseText.trim()) {
        try {
          data = JSON.parse(responseText);
        } catch (e) {
          setLoginError('Server returned invalid response format. Please try again or contact support.');
          throw new Error('Server returned invalid response format');
        }
      } else {
        // Check if this is a CORS issue
        const corsHeaders = response.headers.get('access-control-allow-origin');
        if (!corsHeaders) {
          setLoginError('Network error: Unable to connect to server. Please check your internet connection.');
        } else {
          setLoginError('Empty response from server. Please try again or contact support.');
        }
        throw new Error('Empty response from server');
      }

      // Check if email verification is required
      if (data.requires_verification) {
        // Return data to indicate verification is needed
        return {
          requires_verification: true,
          email: data.email,
          message: data.message
        };
      }

      // Old flow - if access_token is provided (backward compatibility)
      if (data.access_token) {
        // Store the token
        localStorage.setItem('token', data.access_token);
        setIsAuthenticated(true);
        setUser(data.user);

        // Handle payment redirect for paid plans
        if (data.checkout_url) {
          window.location.href = data.checkout_url;
          return { requires_verification: false };
        }

        // Navigate to home page
        navigate('/');
        return { requires_verification: false };
      }

      // If we get here without access_token or verification requirement, something is wrong
      return { requires_verification: false };

    } catch (error) {
      // Enhance error message for network errors
      if (error instanceof TypeError && error.message.includes('fetch')) {
        setLoginError('Network error: Unable to connect to server. Please check your internet connection.');
      }
      throw error;
    }
  }, [navigate]);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setIsAuthenticated(false);
    setUser(null);
    navigate('/login');
  }, [navigate]);

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, login, register, logout, user, loginError }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};